import { actionChannel, all, call, fork, put, select, take, takeEvery, takeLatest, takeLeading } from "redux-saga/effects"
import { push, replace } from "redux-first-history";
import {
  QUESTION_PATIENT,
  QUESTION_RELATIVES,
  SET_QUESTION,
  GET_ALL_DOCUMENT_REQUESTS,
  ADD_DOCUMENTS_TO_QUESTION,
  SET_VITALS_ANSWERS,
  NEXT_QUESTION,
  PREVIOUS_QUESTION,
  CREATE_QUESTION,
  REMOVE_QUESTION,
  REPLACE_QUESTIONS,
  VITALS_COMPLETED,
  SEND_REPORT_TO_MT_CONSENT,
  REMOVE_FAMILY_DOCTOR,
  UNSET_QUESTION,
  RESET_SURVEY_REPLY,
  RESET_SURVEY_STORE,
  SUBMIT_SURVEY_SUCCESS,
  SET_DOCUMENT_NAVIGATION_LOADING,
  FETCH_LATEST_ANAMNESIS,
  JUMP_TO_QUESTION,
  PREFILL_ANAMNESIS,
} from "./constants"
import { RELATIVES_ERROR, RELATIVES_REQUEST, RESET_STORE, SET_CUSTOMER, SET_RELATIVES } from "client/services/constants"
import clientActions from "client/services/actions"
import { ResponsePutCustomer, ResponsePutRelative } from "types/payload"
import { CONFIGURATION_ERROR, CONFIGURATION_SUCCESS } from "client/config/constant"
import { CALL_HANGUP_SUCCESS } from "containers/Call/services/constants"
import {
  clearPersistedSurveySnapshot,
  customerIdentity,
  loadPersistedSurveySnapshot,
  persistSurveySnapshot,
  SurveySnapshot,
  loadPersistedUploadSnapshot,
  loadPersistedVitalsSnapshot,
  UploadSnapshot,
  VitalsSnapshot,
} from "./surveyStorage"
import {
  buildAnamnesisPrefill,
  fetchLatestAnamnesis,
  getLatestAnamnesisCategory,
  getLatestAnamnesisPatientId,
  LatestAnamnesisCache,
} from "./latestAnamnesis"
import { Action, GFlow } from "types/redux"
import { DoctorType, Gender, Relative, TreatingDoctorStatus } from "types/entity"
import { getCustomer, getRelatives } from "client/services/selector"

import {
  getCurrentPatientGender,
  getCurrentPatientGenderStrict,
  getCurrentSelectedPatientId,
  isPregnantQuestionSet,
  getReasonAnswer,
  getCurrentPatient,
  isCurrentPatientUnresolved,
  getDoctorTypeAnswer,
  getCurrentQuestion,
  getUploadDocumentsAnswer,
  getCurrentPatientFamilyDoctor,
} from "./selector"
import actions from "./actions"
import vitalsAction from 'containers/Vitals/services/action';
import {
  allergy_details,
  illness_details,
  INITIAL_PREVENTION_QUESTIONS,
  PATH,
  pregnant,
  QUESTIONS,
} from "../models"
import { ACTION, Answer, DocumentNavigation, Question, QUESTION_TYPE } from "../types"
import { CustomerType } from "types/props"
import { request } from "lib/request"
import uploadActions from 'containers/Upload/services/actions';
import { ADD_DOCUMENTS_TO_STORE, ADD_PHONE_PHOTO, MAIN_PATIENT_ID, SUBMIT_DOCUMENT_FAILURE, SUBMIT_DOCUMENT_SUCCESS } from "containers/Upload/services/constants"
import {
  COMPLETE_PATIENT_INFO_PAGE,
  SEARCH_DOCTOR_PAGE,
  SET_RELATIVE_PAGE,
  SURVEY_PAGE,
  UPLOAD_DOC_PAGE,
} from "core/constants"
import {
  getSelectedPatientVitals,
  getVitalsIdListByPatientId,
} from "containers/Vitals/services/selector"
import { getVitalsNeededByReason } from "containers/Vitals/utils";
import { ADD_VITALS, SET_PREVENTION_REPORT, UNSET_VITALS_BY_TYPE_SAGA } from "containers/Profile/services/constants";
import { ClientStore, ConfigurationStore, SurveyStore, VitalsStore } from "types/store";
import { age, getMissingBirthTraits } from "lib/utils";
import {
  DERMATO_QUESTIONS,
  dermato_zone_evolution_duration,
  dermato_history_details,
} from "../dermato_models"
import { CHECKUP_QUESTIONS } from "../checkup_models"

import { transformMobileSurveyToKiosk } from "../utils";
import { cdn } from "core/cdn";
import { GET_CALL_PRICE } from "containers/Payment/services/constants";
import config from "react-global-configuration"
import { filterDocsForSelectedPatient } from "containers/Upload/services/utils"

function* loadJsonFileFromCdn(url) {
    try {
      const fetchResponse = yield fetch(url);
      return yield fetchResponse.json();
    } catch (err) {
      console.error('Error - ', err);
    }
}

function* createRelativesQuestion({
  payload,
}: Action<Relative[]>): GFlow<Action<Relative[]>> {
  if (payload) {
    yield put({
      type: QUESTION_RELATIVES,
      payload,
    })
  } else yield put({ type: QUESTION_RELATIVES, payload: [] })
}

function* createPatientQuestion(
  action: any
): GFlow<any> {
  if (action.payload)
    yield all([put({ type: QUESTION_PATIENT, payload: action.payload })])
}

function* manageSurveyQuestions(
  action,
): GFlow<Action<Question | "patient" | "relative"> | any> {
  const { id: currentQuestionPath, ...question } =
    yield select(getCurrentQuestion)
  const doctorType = yield select(getDoctorTypeAnswer)
  if (currentQuestionPath === PATH.doctor_type) {
    yield displaySpecialityQuestions()
  }

  if(doctorType === DoctorType.DERMATOLOGY) {
    const rpps = localStorage.getItem("pharmacist_rpps")
    const finess = localStorage.getItem("pharmacist_finess")
    if (rpps && finess && currentQuestionPath !== PATH.dermato_pharmacist) {
      yield put(actions.remove(PATH.dermato_pharmacist))
    }
  }

  if (question?.ui?.view === "questions_line") {
    yield manageSubquestions(action)
  }
  
  // Remove the vitals question when every needed vital is already recorded
  // and none has to be asked again (e.g. the weight of a patient under 18).
  if (
    currentQuestionPath === PATH.reason ||
    currentQuestionPath === PATH.illness
  ) {
    const vitals = yield select(getSelectedPatientVitals)
    const currentReason = yield select(getReasonAnswer)
    const currentPatient = yield select(getCurrentPatient)
    const isPatientUnresolved = yield select(isCurrentPatientUnresolved)
    const vitalsNeeded = getVitalsNeededByReason(
      currentReason,
      currentPatient?.birthdate,
    )
    if (
      // the selected relative is still unknown (relatives not loaded): keep
      // the question rather than treating a possible minor as an adult
      !isPatientUnresolved &&
      !vitalsNeeded.find((v) => v.alwaysNeeded) &&
      !vitalsNeeded.find((v) => !vitals?.[v.label]?.value.raw)
    ) {
      yield put(actions.remove(PATH.vitals))
    }
  }

  if (currentQuestionPath === PATH.patient) {
    yield put(uploadActions.clearLink())
    // anamnesis answers, recorded vitals and uploaded documents apply to the
    // previously selected patient/relative only
    yield put(actions.resetAnamnesis())
    const currentPatientId = yield select(getCurrentSelectedPatientId)
    const checkupPatientId = yield select(
      ({ vitals }: { vitals: Partial<VitalsStore> }) =>
        vitals?.checkupVitals?.patientId,
    )
    const keepCheckupVitals =
      doctorType !== DoctorType.CHECKUP &&
      checkupPatientId !== undefined &&
      (currentPatientId === undefined || currentPatientId === checkupPatientId)
    if (!keepCheckupVitals) {
      yield put(vitalsAction.resetVitals())
    }
    yield put(uploadActions.reset())
    yield put(actions.clearRestoredDocSelection())
    yield put(
      vitalsAction.getVitalsByType({
        relative_id: currentPatientId !== 0 ? currentPatientId : undefined,
        type: "weight",
      }),
    )
    // anamnesis answers apply to the previously selected patient/relative only
    yield put(actions.resetAnamnesis())
    yield put(actions.fetchLatestAnamnesis())
  }
  if (
    [PATH.patient, PATH.inform].includes(currentQuestionPath)
    && doctorType !== DoctorType.PHARMACIST
  ) {
    const currentPatientGender = yield select(getCurrentPatientGender)
    const selectedPatientId = yield select(getCurrentSelectedPatientId)
    const missingBirthTraits = getMissingBirthTraits(
      yield select(getCurrentPatient),
    )
    if (
      currentQuestionPath === PATH.patient &&
      selectedPatientId !== undefined &&
      selectedPatientId !== MAIN_PATIENT_ID &&
      (![Gender.MALE, Gender.FEMALE].includes(currentPatientGender) ||
        missingBirthTraits.length)
    ) {
      yield put(push(SET_RELATIVE_PAGE))
    }
    if (
      currentQuestionPath === PATH.patient &&
      selectedPatientId === MAIN_PATIENT_ID &&
      missingBirthTraits.length
    ) {
      yield put(push(COMPLETE_PATIENT_INFO_PAGE))
    }
    if ([Gender.MALE, Gender.FEMALE].includes(currentPatientGender))
      yield displayPregnantQuestion(currentPatientGender) as any

    const currentCustomerId = yield select(getCurrentSelectedPatientId)
    if (currentCustomerId !== undefined)
      yield modifyTitleQuestion(currentCustomerId, currentPatientGender) as any
  }
  if (currentQuestionPath === PATH.doctor_available) {
    const familyDoctor = yield select(getCurrentPatientFamilyDoctor);    
    if (action.payload?.length > 0 &&
        (action.payload[0].value == TreatingDoctorStatus.DOCTOR_AWAY_CONSENT 
          || action.payload[0].value == TreatingDoctorStatus.DOCTOR_UNAVAILABLE
        ) &&
        (!familyDoctor || Object.keys(familyDoctor).length == 0)
      ) {
        yield put(push(SEARCH_DOCTOR_PAGE))
      }
  }

  // Prevention (Bilan Prévention): personalize the remaining questions to the
  // patient's answers (selected themes + conditional questions).
  if (/^prevention-report-[0-9]+_[0-9]+/.test(currentQuestionPath)) {
    yield syncPreventionQuestions()
  }
}

/*
 * Latest anamnesis prefill: fetched when the patient is selected, then applied
 * to each anamnesis question when it is displayed and still unanswered.
 */
function* loadLatestAnamnesis(): GFlow<any> {
  const patientId = getLatestAnamnesisPatientId(
    yield select(getCurrentSelectedPatientId),
  )
  const category = getLatestAnamnesisCategory(yield select(getDoctorTypeAnswer))
  // every account holder has the patient id 0: bind the answer to the account
  const customerId = customerIdentity(yield select(getCustomer))
  if (
    patientId === undefined ||
    category === undefined ||
    customerId === undefined
  )
    return
  try {
    const data = yield call(fetchLatestAnamnesis, category, patientId)
    // no previous teleconsultation of this category: nothing to prefill
    if (!data) return
    const selectedPatientId = getLatestAnamnesisPatientId(
      yield select(getCurrentSelectedPatientId),
    )
    // a late answer for another patient or account is dropped
    if (
      selectedPatientId !== patientId ||
      customerIdentity(yield select(getCustomer)) !== customerId
    )
      return
    yield put(
      actions.setLatestAnamnesis({ customerId, patientId, category, data }),
    )
    yield call(applyLatestAnamnesisPrefill)
  } catch (error) {
    // the prefill is optional: the patient answers the questions as usual
    console.error(error, { route: config.get("patient.get.latest_anamnesis") })
  }
}

function* applyLatestAnamnesisPrefill(): GFlow<any> {
  const latest: LatestAnamnesisCache | undefined = yield select(
    ({ survey }: { survey: SurveyStore }) => survey.latestAnamnesis,
  )
  if (!latest) return
  const patientId = getLatestAnamnesisPatientId(
    yield select(getCurrentSelectedPatientId),
  )
  const category = getLatestAnamnesisCategory(yield select(getDoctorTypeAnswer))
  if (
    latest.customerId !== customerIdentity(yield select(getCustomer)) ||
    latest.patientId !== patientId ||
    latest.category !== category
  )
    return
  const question: Question | undefined = yield select(getCurrentQuestion)
  const { answers, prefill: provenance } = yield select(
    ({ survey }: { survey: SurveyStore }) => ({
      answers: survey.answers || [],
      prefill: survey.prefill,
    }),
  )
  // applied at most once per question and patient, never over an answer
  const prefill = buildAnamnesisPrefill(
    question,
    latest.data,
    answers as Answer[],
    provenance,
  )
  if (!prefill) return
  yield put(actions.prefillAnamnesis(prefill))
  if (question?.ui?.view === "questions_line") {
    // add (or drop) the details questions as the patient's replies would, one
    // reply at a time so that each insertion sees the updated path
    for (const reply of prefill.value) {
      yield call(manageSubquestions, {
        type: PREFILL_ANAMNESIS,
        payload: [reply],
      } as any)
    }
  }
}

function* modifyTitleQuestion(
  customerId: number,
  gender?: Gender,
): GFlow<Action<CustomerType>> {
  if (customerId === 0) {
    yield put(actions.for("patient"))
    // customer is the main patient
  } else {
    // customer is a relative
    if (gender || gender === Gender.MALE)
      yield put(actions.for("relative_male"))
    if (gender === Gender.FEMALE) yield put(actions.for("relative_female"))
  }
}

function* manageSubquestions(action: Action<Question>): any {
  const surveyPath = yield select(({survey} : {survey: SurveyStore}) => survey?.path)
  for (const { title: key, value } of action.payload as any) {
    if (key === PATH.illness) {
      let options = {};
      if(surveyPath.includes(PATH.allergy)) {
        options = { before: PATH.allergy };
      } else {
        options = { after: PATH.anamnesis_questions };
      }
      yield value
        ? put(
            actions.create({
              question: { ...illness_details, id: PATH.illness },
              options,
            }),
          )
        : put(actions.remove(PATH.illness))
    }
    if (key === PATH.allergy) {
      let options = {};
      if(surveyPath.includes(PATH.illness)) {
        options = { after: PATH.illness }
      } else {
        options = { after: PATH.anamnesis_questions }
      }
      yield value
        ? put(
            actions.create({
              question: { ...allergy_details, id: PATH.allergy },
              options,
            }),
          )
        : put(actions.remove(PATH.allergy))
    }
    if (key === PATH.dermato_zone_evolution) {
      yield value
        ? put(
            actions.create({
              question: dermato_zone_evolution_duration,
              options: { after: PATH.dermato_problem_duration },
            }),
          )
        : put(actions.remove(PATH.dermato_zone_evolution_duration))
    }
    if (key === PATH.dermato_history) {
      yield value
        ? put(
            actions.create({
              question: { ...dermato_history_details, id: PATH.dermato_history },
              options: { after: PATH.dermato_anamnesis_questions },
            }),
          )
        : put(actions.remove(PATH.dermato_history))
    }
  }
}

function* displaySpecialityQuestions() {
  const doctorType = yield select(getDoctorTypeAnswer)
  if (doctorType === DoctorType.DERMATOLOGY) {
    yield put(actions.replaceQuestions(DERMATO_QUESTIONS))
  } else if(doctorType === DoctorType.PHARMACIST) {
    yield put(actions.replaceQuestions(INITIAL_PREVENTION_QUESTIONS));
  } else if (doctorType === DoctorType.CHECKUP) {
    yield put(actions.replaceQuestions(CHECKUP_QUESTIONS))
  } else {
    yield put(actions.replaceQuestions(QUESTIONS))
  }

  // Not sure about this
  const customer = yield select(getCustomer)
  if (customer) {
    yield put({ type: QUESTION_PATIENT, payload: customer })
  }
}

function* displayPregnantQuestion(gender: Gender): any {
  const isQuestionPregnantSet = yield select(isPregnantQuestionSet)
  const doctorType = yield select(getDoctorTypeAnswer)
  // Checkup does not have a pregnant question
  if (doctorType === DoctorType.CHECKUP) {
    return
  }
  const options =
    doctorType === DoctorType.DERMATOLOGY
      ? { after: PATH.anamnesis_questions }
      : { before: PATH.upload_documents }
  if (gender === Gender.FEMALE) {
    if (!isQuestionPregnantSet) {
      yield put(
        actions.create({ question: pregnant, options }), // options: { after: PATH.drug }
      )
    }
  } else {
    if (isQuestionPregnantSet) {
      yield put(actions.remove(PATH.pregnant))
    }
  }
}

function* resolveDocumentNavigation(
  navigation: DocumentNavigation,
  documentsCount: number,
) {
  const currentQuestion = yield select(getCurrentQuestion)

  if (navigation === "forward") {
    if (currentQuestion?.id !== PATH.upload_documents) return
    if (documentsCount > 0) {
      yield put(actions.next())
      return
    }

    const doctorType = yield select(getDoctorTypeAnswer)
    const uploadPage =
      doctorType === DoctorType.DERMATOLOGY
        ? `${UPLOAD_DOC_PAGE}?path=dermato`
        : UPLOAD_DOC_PAGE
    yield put(replace(uploadPage))
    return
  }

  if (
    documentsCount === 0 &&
    [PATH.selected_documents, PATH.selected_documents_prevention].includes(
      currentQuestion?.id,
    )
  ) {
    yield put(actions.previous())
  } else if (currentQuestion?.id === PATH.upload_documents) {
    const surveyPath: string[] = yield select(
      ({ survey }: { survey: SurveyStore }) => survey.path || [],
    )
    const hasSelectionStep = surveyPath.includes(PATH.selected_documents)
    // Only a "Oui" reply opens the selection sub-step. Without it (dermatology
    // photo flow) the documents refresh above still appends that sub-step to
    // the path, so drop it instead of following it.
    const wantsExistingDocuments =
      (yield select(getUploadDocumentsAnswer)) === true
    if (!wantsExistingDocuments) {
      if (hasSelectionStep) {
        yield put(actions.removeFromPath(PATH.selected_documents))
      }
    } else if (documentsCount > 0 && hasSelectionStep) {
      yield put(actions.next())
    }
  }
  yield put(replace(SURVEY_PAGE))
}

function* getAllDocuments(action: any) {
  const currentCustomerId = yield select(getCurrentSelectedPatientId)
  const navigation = action.payload as DocumentNavigation | undefined
  if (navigation) {
    yield put({ type: SET_DOCUMENT_NAVIGATION_LOADING, payload: true })
  }
  try {
    const response = yield request(config.get("patient.uploadedDocuments"), {
      method: "GET",
    })
    const newResponse = filterDocsForSelectedPatient(
      response,
      currentCustomerId,
    )
    yield put({ type: ADD_DOCUMENTS_TO_STORE, payload: newResponse })
    yield put({ type: ADD_DOCUMENTS_TO_QUESTION, payload: newResponse })
    if (navigation) {
      yield call(resolveDocumentNavigation, navigation, newResponse.length)
    }
  } catch (err) {
    console.error(err, {
      route: config.get("patient.uploadedDocuments"),
    })
    if (navigation === "back") {
      yield put(replace(SURVEY_PAGE))
    }
  } finally {
    if (navigation) {
      yield put({ type: SET_DOCUMENT_NAVIGATION_LOADING, payload: false })
    }
  }
}

function* setVitalsAnswers() {
  const selectedVitalsId = yield select(getVitalsIdListByPatientId)
  yield put(actions.unsetStepReplies({ id: PATH.vitals }))
  yield put(
    actions.setStepReplies({ replies: selectedVitalsId, id: PATH.vitals }),
  )
}

function* unsetVitalsAnswers({ payload, type }) {
  const currentPatientId = yield select(getCurrentSelectedPatientId)
  yield put(
    vitalsAction.unsetVitalsByTypeReducer({
      type: payload.type,
      patient_id: currentPatientId,
    }),
  )
}

function* setPreventionReportSurveyByAge(age: any): any {
  let templateToUse = {};
  if(age >= 18 && age <= 25) {
    templateToUse = yield loadJsonFileFromCdn(cdn("/templates/mon_bilan_prevention_autoquestionnaire_18_25ans.json"));
  }
  if(age >= 45 && age <=50) {
    templateToUse = yield loadJsonFileFromCdn(cdn("/templates/mon_bilan_prevention_autoquestionnaire_45_50ans.json"))
  }

  if(age >=60 && age <=65) {
    templateToUse = yield loadJsonFileFromCdn(cdn("/templates/mon_bilan_prevention_autoquestionnaire_60_65ans.json"))
  }

  if(age >= 70 && age <=75) {
    templateToUse = yield loadJsonFileFromCdn(cdn("/templates/mon_bilan_prevention_autoquestionnaire_70_75ans.json"));
  }
  const gender = yield select(getCurrentPatientGenderStrict)
  yield setPreventionReportSurveyTemplate(templateToUse, gender);
  return;
}

// Ordered prevention questions (with personalization metadata) for the current
// patient. Kept here so the dynamic reconciliation can re-add a question after
// the patient changes an earlier answer (theme selection / conditional parent).
let preventionPersonalizationPool: Question[] = []

function* setPreventionReportSurveyTemplate(template, gender?: any): any {
    const existingQuestion = yield select(({survey}: {survey: SurveyStore}) => survey?.questions?.filter((q) => /prevention-report-[0-9]{1,}_[0-9]{1,}/.test(q.id)))
    // remove previous path before starting a new one
    if(existingQuestion) {
      for(let i=0; i<= existingQuestion.length; i++) {
        yield put(actions.unset(existingQuestion[i]));
        yield put(actions.remove(existingQuestion[i]?.id));
      }
      yield put(actions.remove('upload_documents_prevention'));
      yield put(actions.remove('prevention-document-loader'));
      yield put(actions.remove('prevention-survey-end'));
    }
    // remove existing question
    const questions = transformMobileSurveyToKiosk(template, { gender });
    preventionPersonalizationPool = questions.filter((q) =>
      /^prevention-report-[0-9]+_[0-9]+/.test(q.id),
    )
    for(let i =0; i< questions.length; i++){
      yield put(actions.create({question: questions[i]}));
    }
    yield syncPreventionQuestions()
    yield put(actions.next())
}

// Reconciles the visible prevention questions with the patient's answers (theme, conditional questions)
function* syncPreventionQuestions(): any {
  if (!preventionPersonalizationPool.length) return
  const currentQuestions: Question[] = yield select(
    ({ survey }: { survey: SurveyStore }) => survey?.questions,
  )
  const answers: Answer[] = yield select(
    ({ survey }: { survey: SurveyStore }) => survey?.answers,
  )

  const themeSelectorQuestion = preventionPersonalizationPool.find(
    (q) => q.themeSelector,
  )
  const selectedThemesAnswer = themeSelectorQuestion
    ? answers?.find((a) => a.questionId === themeSelectorQuestion.id)?.value
    : undefined
  const selectedThemes =
    selectedThemesAnswer && selectedThemesAnswer.length
      ? selectedThemesAnswer
      : null

  const profileGender = yield select(getCurrentPatientGenderStrict)
  const genderQuestion = preventionPersonalizationPool.find(
    (q) => q.autofillGender,
  )
  const answeredGender = genderQuestion
    ? (genderQuestion.replies || []).find((r: any) =>
        answers
          ?.find((a) => a.questionId === genderQuestion.id)
          ?.value?.includes(r.value),
      )?.gender
    : undefined
  const effectiveGender = profileGender ?? answeredGender

  const isDesired = (q: Question): boolean => {
    // Gender question: only asked when the profile has no usable gender.
    if (q.autofillGender) return profileGender === undefined
    if (
      q.genderOnly !== undefined &&
      effectiveGender !== undefined &&
      q.genderOnly !== effectiveGender
    ) {
      return false
    }
    if (q.conditionalOn) {
      const parentAnswer = answers?.find(
        (a) => a.questionId === q.conditionalOn!.questionId,
      )?.value
      return !!(
        parentAnswer &&
        parentAnswer.length &&
        q.conditionalOn.showIfValues.some((v) => parentAnswer.includes(v))
      )
    }
    if (q.themeSelector || q.mandatory) return true
    if (q.themes?.length) {
      if (selectedThemes === null) return false
      return q.themes.some((t) => selectedThemes.includes(t))
    }
    return false
  }

  let anchorId: string | undefined
  for (const q of preventionPersonalizationPool) {
    const desired = isDesired(q)
    const present = currentQuestions?.some((cur) => cur.id === q.id)
    if (desired && !present) {
      yield put(
        actions.create({
          question: q,
          options: anchorId ? ({ after: anchorId } as any) : undefined,
        }),
      )
    } else if (!desired && present) {
      yield put(actions.remove(q.id))
    }
    if (desired) anchorId = q.id
  }
}

function* setPreventionReport() {
  const customer = yield select(({ client }: { client: ClientStore }) => client.customer)
  yield createPatientQuestion({ payload: customer });
  yield put(push(SURVEY_PAGE));
}

function* onVitalsCompleted({ payload }: any): any {
  if (payload?.pendingVitalCreate) {
    yield take(ADD_VITALS)
  }
  yield call(setVitalsAnswers)
  yield put(actions.next())
  yield put(push(SURVEY_PAGE))
}
        
function* handlePreventionDocumentLoader(): any {
  const currentQuestion = yield select(getCurrentQuestion)
  if (currentQuestion?.id === 'prevention-report-loader:loader') {
    const currentPatient = yield select(getCurrentPatient);
    if (currentPatient !== undefined) {
      const customerAge = age(currentPatient?.birthdate);
      yield setPreventionReportSurveyByAge(customerAge) as any
    } else {
      yield put(actions.next())
    }
  }
  if (currentQuestion?.id === 'prevention-document-loader') {
    const genderQuestion = preventionPersonalizationPool.find((q) => q.autofillGender)
    const alreadyAnswered = yield select(({ survey }: { survey: SurveyStore }) =>
      survey?.answers?.some(
        (a) => a.questionId === genderQuestion?.id && a.value?.length,
      ),
    )
    if (genderQuestion && !alreadyAnswered) {
      const gender = yield select(getCurrentPatientGenderStrict)
      const reply = (genderQuestion.replies || []).find((r: any) => r.gender === gender)
      if (reply) {
        yield put(actions.create({ question: genderQuestion }))
        yield put(actions.setStepReplies({ replies: [reply], id: genderQuestion.id as any }))
        yield put(actions.removeFromPath(genderQuestion.id))
      }
    }
    yield put(uploadActions.uploadPreventionReport());
    yield take([SUBMIT_DOCUMENT_SUCCESS, SUBMIT_DOCUMENT_FAILURE])
    yield put(actions.next())
  }
}

function* sendReportToMTConsent({payload: { consent } }: any): any {
  const doctor_available = yield select(({survey}: {survey: SurveyStore}) => survey.answers.find((a) => a.questionId === PATH.doctor_available)?.value[0])
  if(![TreatingDoctorStatus.DOCTOR_AWAY_CONSENT, TreatingDoctorStatus.DOCTOR_AWAY_NO_CONSENT].includes(doctor_available)) return;
  if(doctor_available === TreatingDoctorStatus.DOCTOR_AWAY_CONSENT && !consent) {
    yield put({type: UNSET_QUESTION, payload: { id: PATH.doctor_available }})
    yield put({type: SET_QUESTION, payload: {replies: [{value: TreatingDoctorStatus.DOCTOR_AWAY_NO_CONSENT}], id: PATH.doctor_available }})
  }
  if(doctor_available === TreatingDoctorStatus.DOCTOR_AWAY_NO_CONSENT && consent) {
    yield put({type: UNSET_QUESTION, payload: { id: PATH.doctor_available }})
    yield put({type: SET_QUESTION, payload: {replies: [{value: TreatingDoctorStatus.DOCTOR_AWAY_CONSENT}], id: PATH.doctor_available }})
  }
  yield put({type: GET_CALL_PRICE })
}

// Clears the treating doctor of the selected patient (account holder or
// relative), then refreshes the profile so the survey shows the options of a
// patient without treating doctor
function* removeFamilyDoctor({ payload: { onSuccess, onError } }: Action<{ onSuccess: () => void; onError: () => void }>): any {
  const patientId: number | undefined = yield select(getCurrentSelectedPatientId)
  if (patientId === undefined) {
    onError()
    return
  }
  const route = patientId === 0
    ? config.get("patient.put.patient")
    : `${config.get("patient.put.relative")}/${patientId}`
  try {
    if (patientId === 0) {
      const response: ResponsePutCustomer = yield call(request, route, {
        method: "PUT",
        payload: { family_doctor_rpps: "" },
      })
      if (!response.customer) throw new Error("Missing customer in response")
      yield put(clientActions.setCustomer(response.customer))
    } else {
      // A 404 resolves without throwing: check the payload before refreshing
      const response: ResponsePutRelative = yield call(request, route, {
        method: "PUT",
        payload: { id: patientId, family_doctor_rpps: "" },
      })
      if (!response.relative) throw new Error("Missing relative in response")
      yield put({ type: RELATIVES_REQUEST })
      const refreshed = yield take([SET_RELATIVES, RELATIVES_ERROR])
      if (refreshed.type === RELATIVES_ERROR) throw new Error("Relatives refresh failed")
    }
  } catch (error) {
    console.error(error, { route })
    onError()
    return
  }
  onSuccess()
}

/*
 * Survey persistence: the whole survey state is snapshot in IndexedDB so a
 * window reload restores the current question and the answers already given.
 */
const SURVEY_PERSIST_ACTIONS = [
  SET_QUESTION,
  UNSET_QUESTION,
  NEXT_QUESTION,
  PREVIOUS_QUESTION,
  CREATE_QUESTION,
  REMOVE_QUESTION,
  REPLACE_QUESTIONS,
  RESET_SURVEY_REPLY,
]

const SURVEY_CLEAR_ACTIONS = [
  RESET_SURVEY_STORE,
  RESET_STORE,
  SUBMIT_SURVEY_SUCCESS,
  CALL_HANGUP_SUCCESS,
]

function* syncSurveyStorage(action: Action<unknown>): GFlow<any> {
  if (SURVEY_CLEAR_ACTIONS.includes(action.type)) {
    if (action.type === CALL_HANGUP_SUCCESS) {
      // anamnesis answers belong to the consultation that just ended
      yield put(actions.resetAnamnesis())
    }
    yield call(clearPersistedSurveySnapshot)
    return
  }
  const survey = yield select(({ survey }: { survey: SurveyStore }) => survey)
  const customer = yield select(getCustomer)
  yield call(persistSurveySnapshot, survey || {}, customerIdentity(customer))
}

function* hydrateSurveyFromStorage(): GFlow<any> {
  const snapshot: SurveySnapshot | undefined = yield call(
    loadPersistedSurveySnapshot,
  )
  if (!snapshot) {
    // The survey snapshot is absent or expired: the vitals and upload
    // snapshots describe the same consultation, wipe them from disk too
    yield call(clearPersistedSurveySnapshot)
    return
  }
  if (snapshot.status !== "on_going" || !snapshot.currentId) {
    yield call(clearPersistedSurveySnapshot)
    return
  }
  // Wait for the installation flow to restore the patient session
  let customer = yield select(getCustomer)
  while (!customer) {
    yield take(SET_CUSTOMER)
    customer = yield select(getCustomer)
  }
  // The snapshot belongs to another patient: drop it instead of restoring it
  if (
    !snapshot.customerId ||
    customerIdentity(customer) !== snapshot.customerId
  ) {
    yield call(clearPersistedSurveySnapshot)
    return
  }
  // The configuration fetch triggered by the installation re-initializes the
  // survey store: hydrate only once it has settled
  const configurationLoading = yield select(
    ({ configuration }: { configuration: ConfigurationStore }) =>
      configuration.loading,
  )
  if (configurationLoading) {
    yield take([CONFIGURATION_SUCCESS, CONFIGURATION_ERROR])
  }
  yield put(actions.hydrateSurvey(snapshot))
  // Vitals and uploaded documents recorded before the reload belong to the
  // same in-progress consultation: restore them alongside the survey
  const vitalsSnapshot: VitalsSnapshot | undefined = yield call(
    loadPersistedVitalsSnapshot,
  )
  if (vitalsSnapshot?.vitals && vitalsSnapshot.customerId === snapshot.customerId) {
    yield put(vitalsAction.hydrateVitals(vitalsSnapshot.vitals))
  }
  const uploadSnapshot: UploadSnapshot | undefined = yield call(
    loadPersistedUploadSnapshot,
  )
  if (uploadSnapshot && uploadSnapshot.customerId === snapshot.customerId) {
    yield put(uploadActions.hydrateUpload(uploadSnapshot))
  }
  // Patient and relatives replies hold non-serializable content (icons),
  // rebuild them from the client store
  yield put({ type: QUESTION_PATIENT, payload: customer })
  const relatives = yield select(getRelatives)
  if (relatives.length) {
    yield put({ type: QUESTION_RELATIVES, payload: relatives })
  }
  // Refetch the selected patient vitals so the vitals page resumes at the
  // first missing vital instead of asking recorded ones again
  const currentPatientId = yield select(getCurrentSelectedPatientId)
  if (currentPatientId !== undefined) {
    yield put(
      vitalsAction.getVitalsByType({
        relative_id: currentPatientId !== 0 ? currentPatientId : undefined,
        type: "weight",
      }),
    )
  }
  // A loader question auto-advances on NEXT_QUESTION only:
  // re-run its logic or the survey would stay stuck on the loader
  const current = yield select(getCurrentQuestion)
  if (current?.type === QUESTION_TYPE.loader) {
    yield call(handlePreventionDocumentLoader)
  }
  // Unconfirmed prefilled values are not persisted: fetch them again. The
  // restored answers win, the prefill only completes untouched questions
  yield put(actions.fetchLatestAnamnesis())
}

function* surveyWatcher(): Generator<any> {
  yield fork(hydrateSurveyFromStorage)
  yield takeLatest(SET_RELATIVES, createRelativesQuestion)
  yield takeLatest(SET_CUSTOMER, createPatientQuestion)
  yield takeLatest(SET_QUESTION, manageSurveyQuestions)
  yield takeLatest(
    [...SURVEY_PERSIST_ACTIONS, ...SURVEY_CLEAR_ACTIONS],
    syncSurveyStorage,
  )
  yield takeEvery(NEXT_QUESTION, handlePreventionDocumentLoader)
  yield takeLatest(FETCH_LATEST_ANAMNESIS, loadLatestAnamnesis)
  yield takeEvery(
    [NEXT_QUESTION, PREVIOUS_QUESTION, JUMP_TO_QUESTION],
    applyLatestAnamnesisPrefill,
  )
  yield takeLatest(GET_ALL_DOCUMENT_REQUESTS, getAllDocuments)
  yield takeLatest(SET_PREVENTION_REPORT, setPreventionReport)
  yield takeLatest(SET_VITALS_ANSWERS, setVitalsAnswers)
  yield takeLatest(UNSET_VITALS_BY_TYPE_SAGA, unsetVitalsAnswers)
  yield takeLatest(VITALS_COMPLETED, onVitalsCompleted)
  yield takeLatest(SEND_REPORT_TO_MT_CONSENT, sendReportToMTConsent)
  yield takeLeading(REMOVE_FAMILY_DOCTOR, removeFamilyDoctor)
}

export default surveyWatcher
